/**
 * Header. Backend + WS status pills, account selector, workspace controls,
 * theme toggle, user menu. Mobile: hamburger triggers sidebar Sheet,
 * status collapses to a single dot, account selector stays prominent.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Circle, ChevronDown, Building2, Monitor, Sun, Moon, LogOut, User,
  LayoutGrid, RotateCcw, Maximize2, Menu, Settings as SettingsIcon,
  Briefcase,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAggregatedRisk } from '@/hooks/useAggregated'
import { getHealth, getCaptureStatus } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'
import { logout } from '@/lib/api/auth'
import { useAuthStore } from '@/stores/authStore'
import {
  useSettingsStore, type Theme, FONT_SIZE_PX,
  getSymbolFontFamily, loadSymbolWebfont,
} from '@/stores/settingsStore'
import { useAccountStore, ACCOUNT_ALIASES, type AccountAlias } from '@/stores/accountStore'
import { useBrokerAccounts } from '@/hooks/useBrokerAccounts'
import { useTenantStore } from '@/stores/tenantStore'
import { getMe, getTenants } from '@/lib/api/tenants'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { cn } from '@/lib/utils'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Sidebar } from './Sidebar'

interface HeaderProps {
  isMobile: boolean
}

export function Header({ isMobile }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const username = useAuthStore((s) => s.username)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const theme = useSettingsStore((s) => s.theme)
  const setTheme = useSettingsStore((s) => s.setTheme)
  const fontSize = useSettingsStore((s) => s.fontSize)
  const symbolFont = useSettingsStore((s) => s.symbolFont)
  const navigate = useNavigate()
  const activeAlias = useAccountStore((s) => s.activeAlias)
  const setActiveAlias = useAccountStore((s) => s.setActiveAlias)
  const setAccounts = useAccountStore((s) => s.setAccounts)
  const currentTenant = useTenantStore((s) => s.currentTenant)
  const availableTenants = useTenantStore((s) => s.availableTenants)
  const setCurrentTenant = useTenantStore((s) => s.setCurrentTenant)
  const setAvailableTenants = useTenantStore((s) => s.setAvailableTenants)
  const setIsSuperuser = useTenantStore((s) => s.setIsSuperuser)
  const resetLayout = useWorkspaceStore((s) => s.resetLayout)

  // Multi-tenant hydration: /auth/me on mount populates the tenant store.
  const meQuery = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: getMe,
    refetchInterval: 5 * 60_000,
  })
  const tenantsQuery = useQuery({
    queryKey: queryKeys.tenants.list(),
    queryFn: getTenants,
    refetchInterval: 5 * 60_000,
  })
  useEffect(() => {
    if (meQuery.data) {
      const me = meQuery.data
      setIsSuperuser(me.user.is_superuser)
      // If we don't have a current tenant in store, adopt the server-side one.
      if (!currentTenant) {
        setCurrentTenant({
          id: me.current_tenant.id,
          slug: me.current_tenant.slug,
          name: me.current_tenant.name,
        })
      }
    }
  }, [meQuery.data, currentTenant, setCurrentTenant, setIsSuperuser])
  useEffect(() => {
    if (tenantsQuery.data) {
      setAvailableTenants(
        tenantsQuery.data.tenants.map((t) => ({
          id: t.id, slug: t.slug, name: t.name,
        })),
      )
    }
  }, [tenantsQuery.data, setAvailableTenants])

  const { data: health } = useQuery({
    queryKey: queryKeys.health.root(), queryFn: getHealth, refetchInterval: 30_000,
  })
  // Native fill-capture / gateway health (replaces the retired bot-WS pill).
  // The poll also self-heals capture wiring after a gateway reconnect.
  const { data: capture } = useQuery({
    queryKey: queryKeys.capture.status(), queryFn: getCaptureStatus, refetchInterval: 30_000,
  })
  const risk = useAggregatedRisk()

  // Canonical alias list comes from Settings → Brokers (active accounts, by
  // display_order). Before the query resolves — or if it returns nothing — fall
  // back to the ACCOUNT_ALIASES literal so the selector is never empty.
  const brokerAccounts = useBrokerAccounts()
  const aliases: AccountAlias[] = useMemo(() => {
    const rows = brokerAccounts.data?.accounts ?? []
    const active = rows
      .filter((a) => a.is_active)
      .sort((a, b) => a.display_order - b.display_order)
      .map((a) => a.alias)
    return active.length > 0 ? active : ACCOUNT_ALIASES
  }, [brokerAccounts.data?.accounts])
  const knownAliases = useMemo(() => new Set<string>(aliases), [aliases])

  // Sync accounts → store (so the sidebar can show per-alias data)
  useEffect(() => {
    if (!risk.data?.by_account) return
    setAccounts(
      risk.data.by_account
        .filter((a) => knownAliases.has(a.alias))
        .map((a) => ({
          alias: a.alias,
          ib_account_id: '',
          account_name: a.alias,
          connected: true,
        })),
    )
  }, [risk.data?.by_account, setAccounts, knownAliases])

  const onLogout = () => {
    // Hit the API while the bearer is still in the store so the response
    // can clear the host-only session cookie. Then drop localStorage.
    void (async () => {
      try {
        await logout()
      } catch {
        /* still leave locally if the API is unreachable */
      }
      clearAuth()
      window.location.href = '/login'
    })()
  }

  const overallOk = health?.status === 'ok'
  const captureOk = !!capture?.ok
  const connKind = capture?.connector || 'gateway'  // CSS uppercases → GATEWAY / IBX

  // Apply theme class to <html>
  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') root.classList.add('dark')
    else if (theme === 'light') root.classList.remove('dark')
    else {
      const sysDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches
      root.classList.toggle('dark', sysDark)
    }
  }, [theme])

  // Apply font-size to <html> (rem base) — scales all Tailwind text-* utilities.
  useEffect(() => {
    document.documentElement.style.fontSize = `${FONT_SIZE_PX[fontSize]}px`
  }, [fontSize])

  // Apply symbol-font as a CSS variable (components opt in via
  // `style={{ fontFamily: 'var(--symbol-font)' }}`) and lazy-load the
  // matching bundled webfont so all 8 choices actually render.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--symbol-font', getSymbolFontFamily(symbolFont),
    )
    loadSymbolWebfont(symbolFont)
  }, [symbolFont])

  return (
    <TooltipProvider delayDuration={200}>
      <header className="flex h-11 md:h-14 items-center justify-between gap-3 border-b border-border bg-card px-3 md:px-4">
        {/* Left: hamburger on mobile + status pills */}
        <div className="flex items-center gap-2 min-w-0">
          {isMobile && (
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="p-2 -ml-2 text-muted-foreground hover:text-foreground" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-56 p-0" hideClose>
                <Sidebar embedded onNavigate={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>
          )}

          {/* Single status dot on mobile, full pills on desktop */}
          {isMobile ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1.5">
                  <Circle className={cn('h-2 w-2', overallOk && captureOk ? 'fill-positive text-positive animate-pulse' : 'fill-warning text-warning')} />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div>backend: {overallOk ? 'ok' : 'down'}</div>
                <div>{connKind}: {captureOk ? 'live' : 'down'}</div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <>
              <StatusPill ok={overallOk} label={`backend ${overallOk ? 'ok' : 'down'}`} />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusPill ok={captureOk} label={`${connKind} ${captureOk ? 'live' : 'down'}`} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <div className="font-medium mb-1">Fill capture / IB gateways</div>
                  {(capture?.gateways ?? []).length === 0 && <div>no gateways</div>}
                  {(capture?.gateways ?? []).map((g) => (
                    <div key={g.alias} className="flex items-center gap-2">
                      <Circle className={cn('h-2 w-2',
                        g.connected ? 'fill-positive text-positive' : 'fill-warning text-warning')} />
                      <span className="font-mono">{g.alias}{g.is_default ? ' (default)' : ''}</span>
                      <span className="text-muted-foreground uppercase text-[10px]">{g.connector}</span>
                      <span className="text-muted-foreground">
                        {g.connected ? (g.capture_wired ? 'capturing' : 'connected') : 'disconnected'}
                      </span>
                    </div>
                  ))}
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>

        {/* Center/right: account selector + workspace + theme + user */}
        <div className="flex items-center gap-1 md:gap-2 min-w-0">
          {/* Tenant selector */}
          {availableTenants.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border text-xs font-mono uppercase tracking-wider hover:bg-accent/50"
                  title="Active tenant scope"
                >
                  <Briefcase className="h-3.5 w-3.5" />
                  <span className="max-w-[160px] truncate">
                    {currentTenant?.name ?? 'tenant'}
                  </span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Active tenant</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  value={currentTenant?.id ?? ''}
                  onValueChange={(v: string) => {
                    const t = availableTenants.find((x) => x.id === v)
                    if (t) setCurrentTenant(t)
                    // Reload to re-run all data fetches with the new tenant header.
                    if (typeof window !== 'undefined') {
                      setTimeout(() => window.location.reload(), 50)
                    }
                  }}
                >
                  {availableTenants.map((t) => (
                    <DropdownMenuRadioItem key={t.id} value={t.id}>
                      {t.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Account selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border text-xs font-mono uppercase tracking-wider hover:bg-accent/50">
                <Building2 className="h-3.5 w-3.5" />
                <span className="max-w-[140px] truncate">{activeAlias ?? 'all accounts'}</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Active account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={activeAlias ?? '__all__'}
                onValueChange={(v: string) => setActiveAlias(v === '__all__' ? null : (v as AccountAlias))}
              >
                <DropdownMenuRadioItem value="__all__">All Accounts</DropdownMenuRadioItem>
                <DropdownMenuSeparator />
                {aliases.map((alias) => (
                  <DropdownMenuRadioItem key={alias} value={alias}>{alias}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Workspace controls — desktop only */}
          {!isMobile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-muted-foreground hover:bg-accent/50 hover:text-foreground" title="Workspace">
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Workspace</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => resetLayout()}>
                  <RotateCcw className="h-4 w-4" /> Reset to dashboard
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Maximize2 className="h-4 w-4 opacity-50" /> Save preset (todo)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Theme */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-accent/50 hover:text-foreground" title="Theme">
                {theme === 'dark' ? <Moon className="h-4 w-4" /> :
                 theme === 'light' ? <Sun className="h-4 w-4" /> :
                 <Monitor className="h-4 w-4" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuLabel>Theme</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={theme} onValueChange={(v: string) => setTheme(v as Theme)}>
                <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-muted-foreground hover:bg-accent/50 hover:text-foreground" title={username ?? 'User'}>
                <User className="h-4 w-4" />
                {!isMobile && username && (
                  <span className="font-mono text-xs">{username}</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{username ?? 'Account'}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/settings')}>
                <SettingsIcon className="h-4 w-4" /> User Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onLogout} className="text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </TooltipProvider>
  )
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  // Colored chip, not a gray pill with a dot — the Trading Bot treatment:
  // the whole chip carries the state (green border + green text when up).
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider border',
      ok
        ? 'border-positive/40 bg-positive/10 text-positive'
        : 'border-destructive/40 bg-destructive/10 text-destructive',
    )}>
      <Circle className={cn('h-1.5 w-1.5', ok ? 'fill-positive text-positive animate-pulse' : 'fill-destructive text-destructive')} />
      {label}
    </span>
  )
}
